import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'

export function DataTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No rows</p>
  const cols = Object.keys(rows[0]).filter(k => k !== '$meta')
  return (
    <div className="overflow-x-auto border rounded-md">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {cols.map(c => (
              <TableHead key={c} className="px-3 text-xs font-mono whitespace-nowrap">{c}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {cols.map(c => {
                const v = row[c]
                const s = v === null || v === undefined ? 'NULL'
                  : typeof v === 'object' ? JSON.stringify(v)
                  : String(v)
                return (
                  <TableCell key={c} className="px-3 py-1.5 text-xs whitespace-nowrap max-w-48 truncate">
                    {v === null || v === undefined
                      ? <span className="text-muted-foreground">NULL</span>
                      : <span className="font-mono">{s.length > 60 ? s.slice(0, 60) + '...' : s}</span>
                    }
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export function OverlayDetail({ overlay }: { overlay: Record<string, unknown> }) {
  const tableNames = Object.keys(overlay)
  if (tableNames.length === 0) return <p className="text-sm text-muted-foreground">Empty overlay</p>
  return (
    <div className="space-y-3">
      {tableNames.map(tbl => {
        const tableOverlay = overlay[tbl] as Record<string, unknown> | undefined
        if (!tableOverlay) return null
        const entries = Object.entries(tableOverlay)
        return (
          <div key={tbl}>
            <div className="flex items-center gap-2 mb-1.5">
              <Badge variant="outline" className="text-xs font-mono">{tbl}</Badge>
              <span className="text-xs text-muted-foreground">{entries.length} {entries.length === 1 ? 'override' : 'overrides'}</span>
            </div>
            <div className="overflow-x-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="px-3 text-xs font-mono w-32">Row ID</TableHead>
                    <TableHead className="px-3 text-xs font-mono">Changed Fields</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map(([rowId, changes]) => (
                    <TableRow key={rowId}>
                      <TableCell className="px-3 py-1.5 text-xs font-mono">{rowId}</TableCell>
                      <TableCell className="px-3 py-1.5 text-xs font-mono text-muted-foreground max-w-96 truncate">
                        {typeof changes === 'object' && changes
                          ? Object.keys(changes as Record<string, unknown>).join(', ')
                          : JSON.stringify(changes)
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )
      })}
    </div>
  )
}

type TableShape = { $meta?: Record<string, unknown>; rows?: Record<string, unknown>[] }
type OverlayShape = { $meta?: Record<string, unknown>; overrides?: Record<string, Record<string, unknown>> }

/** Apply overlay stack to base tables and merge custom tables. Exported for use in ShareDetailPage. */
export function aggregateSeedData(
  baseTables: Record<string, TableShape>,
  overlays: Record<string, Record<string, unknown>>,
  overlayStack: string[],
  customTables: Record<string, TableShape>,
): Record<string, { meta?: Record<string, unknown>; rows: Record<string, unknown>[] }> {
  const result: Record<string, { meta?: Record<string, unknown>; rows: Record<string, unknown>[] }> = {}
  for (const [name, table] of Object.entries(baseTables)) {
    result[name] = {
      meta: table.$meta as Record<string, unknown> | undefined,
      rows: (table.rows || []).map(r => ({ ...r })),
    }
  }

  for (const overlayName of overlayStack) {
    const overlayGroup = overlays[overlayName]
    if (!overlayGroup) continue
    for (const [tableName, tableOverlay] of Object.entries(overlayGroup)) {
      const target = result[tableName]
      if (!target) continue
      const ol = tableOverlay as OverlayShape | Record<string, Record<string, unknown>>
      const overrides = (ol as OverlayShape).overrides || (ol as Record<string, Record<string, unknown>>)
      if (!overrides || typeof overrides !== 'object') continue
      for (const [rowId, fields] of Object.entries(overrides)) {
        if (rowId === '$meta' || typeof fields !== 'object' || !fields) continue
        const idx = target.rows.findIndex(r => r.id === rowId)
        if (idx !== -1) {
          target.rows[idx] = { ...target.rows[idx], ...fields }
        }
      }
    }
  }

  for (const [name, customTable] of Object.entries(customTables)) {
    const customRows = (customTable.rows || []) as Record<string, unknown>[]
    const existing = result[name]
    if (!existing) {
      result[name] = {
        meta: customTable.$meta as Record<string, unknown> | undefined,
        rows: customRows.map(r => ({ ...r })),
      }
      continue
    }
    const customById = new Map(customRows.map(r => [r.id as string, r]))
    existing.rows = existing.rows.map(r => {
      const custom = customById.get(r.id as string)
      return custom ? { ...r, ...custom } : r
    })
  }

  return result
}

function TabledSection({ label, tables }: { label: string; tables: Record<string, TableShape> }) {
  const names = Object.keys(tables)
  if (names.length === 0) return <p className="text-sm text-muted-foreground py-2">No {label.toLowerCase()}</p>
  return (
    <Tabs defaultValue={names[0]}>
      <TabsList className="h-auto flex-wrap">
        {names.map(t => (
          <TabsTrigger key={t} value={t} className="text-xs font-mono">{t}</TabsTrigger>
        ))}
      </TabsList>
      {names.map(t => {
        const table = tables[t]
        const meta = table?.$meta as Record<string, unknown> | undefined
        const rows = (table?.rows || []) as Record<string, unknown>[]
        return (
          <TabsContent key={t} value={t}>
            {meta && (
              <p className="text-xs text-muted-foreground mb-2">
                {meta.description ? <span>{String(meta.description)} &middot; </span> : null}
                {rows.length} {rows.length === 1 ? 'row' : 'rows'}
              </p>
            )}
            <DataTable rows={rows} />
          </TabsContent>
        )
      })}
    </Tabs>
  )
}

/** Renders aggregated (overlay-applied) data tables. Exported for use in ShareDetailPage. */
export function AggregatedSection({ aggregated }: { aggregated: Record<string, { meta?: Record<string, unknown>; rows: Record<string, unknown>[] }> }) {
  const names = Object.keys(aggregated)
  if (names.length === 0) return <p className="text-sm text-muted-foreground py-2">No data</p>
  return (
    <Tabs defaultValue={names[0]}>
      <TabsList className="h-auto flex-wrap">
        {names.map(t => (
          <TabsTrigger key={t} value={t} className="text-xs font-mono">{t}</TabsTrigger>
        ))}
      </TabsList>
      {names.map(t => {
        const table = aggregated[t]
        return (
          <TabsContent key={t} value={t}>
            {table.meta && (
              <p className="text-xs text-muted-foreground mb-2">
                {table.meta.description ? <span>{String(table.meta.description)} &middot; </span> : null}
                {table.rows.length} {table.rows.length === 1 ? 'row' : 'rows'}
              </p>
            )}
            <DataTable rows={table.rows} />
          </TabsContent>
        )
      })}
    </Tabs>
  )
}

/** Seed row detail — shows parsed meta from the seeds table */
export function SeedRowDetail({ row }: { row: Record<string, unknown> }) {
  let meta: Record<string, unknown> = {}

  try { meta = typeof row.meta === 'string' ? JSON.parse(row.meta) : (row.meta as Record<string, unknown>) || {} } catch { /* ignore */ }

  const entries = Object.entries(meta).filter(([, v]) => v !== undefined && v !== null)
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">No metadata</p>

  return (
    <div className="space-y-1.5">
      {meta.currentVersion ? (
        <p className="text-xs text-muted-foreground">
          Version <Badge variant="secondary" className="text-xs font-mono">v{String(meta.currentVersion)}</Badge>
        </p>
      ) : null}
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="contents">
            <span className="text-xs font-mono text-muted-foreground">{key}</span>
            <span className="text-xs font-mono truncate max-w-96">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
